import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api';
import './Leases.css';

// Derive initials from a tenant name (matches the server-side logic in
// server/routes/leases.js so the preview shown here matches what gets baked
// into the filled PDF). "John Doe" -> "JD".
function deriveInitials(name) {
  if (!name) return '';
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(w => w[0].toUpperCase())
    .join('');
}

// Field names that should be auto-populated from the entry's data. Each
// returns its value given (address, fieldData) so we can derive from any
// other field on the same lease entry.
const AUTO_FIELDS = {
  'Address of Property': (addr) => addr,
  'Tenant Signature': (_addr, data) => data?.['Tenant 1 Name'] || '',
  'Tenant Initials': (_addr, data) => deriveInitials(data?.['Tenant 1 Name'] || ''),
};

export default function Leases() {
  const { id: packageId } = useParams();
  const [fields, setFields] = useState([]);
  const [entries, setEntries] = useState([]); // [{ id, address, field_data, ... }]
  const [leases, setLeases] = useState([]);
  const [error, setError] = useState('');
  const [filling, setFilling] = useState({}); // entryId -> bool
  const [templateMissing, setTemplateMissing] = useState(false);
  const saveTimers = useRef({});

  const loadFiles = useCallback(async () => {
    try {
      const res = await api.get('/leases/files', { params: { packageId } });
      setLeases(res.data.leases || []);
    } catch (err) {
      console.error('Failed to load lease files:', err);
    }
  }, [packageId]);

  const loadEntries = useCallback(async () => {
    try {
      const res = await api.get('/leases/entries', { params: { packageId } });
      setEntries(res.data.entries || []);
    } catch (err) {
      console.error('Failed to load lease entries:', err);
    }
  }, [packageId]);

  // Template fields (one-time)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/leases/template-fields');
        if (cancelled) return;
        setFields(res.data.fields || []);
        setTemplateMissing(false);
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 400) {
          setTemplateMissing(true);
          setFields([]);
        } else {
          setError('Failed to load lease template fields.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const debouncedSave = useCallback((entryId, payload) => {
    if (saveTimers.current[entryId]) clearTimeout(saveTimers.current[entryId]);
    saveTimers.current[entryId] = setTimeout(async () => {
      try {
        await api.put(`/leases/entries/${entryId}`, payload);
      } catch (err) {
        console.error('Auto-save lease entry failed:', err);
      }
    }, 600);
  }, []);

  // Layer the auto-derived fields onto a stored field_data blob. Auto field
  // functions take (address, data) so they can derive from any other field
  // (e.g. Tenant Initials from Tenant 1 Name).
  const applyAutoFields = (storedData, address) => {
    const merged = { ...(storedData || {}) };
    for (const [name, fn] of Object.entries(AUTO_FIELDS)) {
      merged[name] = fn(address || '', merged);
    }
    return merged;
  };

  // Returns the data merged with auto fields so the UI always shows the
  // current derived values in the auto-populated inputs
  const getDataFor = (entry) => applyAutoFields(entry.field_data, entry.address);

  const updateField = (entryId, fieldName, value) => {
    setEntries(prev => prev.map(e => {
      if (e.id !== entryId) return e;
      const nextFieldData = applyAutoFields(
        { ...(e.field_data || {}), [fieldName]: value },
        e.address
      );
      const next = { ...e, field_data: nextFieldData };
      debouncedSave(entryId, { fieldData: nextFieldData });
      return next;
    }));
  };

  const updateAddress = (entryId, address) => {
    setEntries(prev => prev.map(e => {
      if (e.id !== entryId) return e;
      const next = { ...e, address };
      next.field_data = applyAutoFields(e.field_data, address);
      debouncedSave(entryId, { address, fieldData: next.field_data });
      return next;
    }));
  };

  const addEntry = async () => {
    setError('');
    try {
      const res = await api.post('/leases/entries', { packageId });
      setEntries(prev => [...prev, res.data.entry]);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add lease.');
    }
  };

  const deleteEntry = async (entry) => {
    if (!window.confirm(`Delete lease for "${entry.address || 'this property'}"? This won't delete any already-filled PDFs in the sidebar.`)) return;
    try {
      await api.delete(`/leases/entries/${entry.id}`);
      setEntries(prev => prev.filter(e => e.id !== entry.id));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete lease.');
    }
  };

  const handleFillPdf = async (entry) => {
    setError('');
    setFilling(f => ({ ...f, [entry.id]: true }));
    try {
      const data = getDataFor(entry);
      await api.post(`/leases/entries/${entry.id}/fill-pdf`, { fieldData: data });
      await loadFiles();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fill PDF.');
    } finally {
      setFilling(f => ({ ...f, [entry.id]: false }));
    }
  };

  const handleDownload = async (lease) => {
    try {
      const res = await api.get(`/leases/files/${lease.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', lease.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download lease.');
    }
  };

  const handleDeleteFile = async (lease) => {
    if (!window.confirm(`Delete filled lease for ${lease.address}?`)) return;
    try {
      await api.delete(`/leases/files/${lease.id}`);
      await loadFiles();
    } catch {
      setError('Failed to delete lease.');
    }
  };

  return (
    <div className="leases-page">
      <h1 className="page-title">Leases</h1>

      {templateMissing ? (
        <div className="warning-box">No lease template uploaded. Go to Settings to upload a fillable PDF lease template.</div>
      ) : (
        <div className="leases-layout">
          <div className="leases-main">
            {error && <div className="error-box">{error}</div>}

            {entries.map(entry => {
              const data = getDataFor(entry);
              return (
                <div key={entry.id} className="lease-card">
                  <div className="lease-card-header">
                    <input
                      type="text"
                      className="lease-card-address-input"
                      value={entry.address || ''}
                      onChange={e => updateAddress(entry.id, e.target.value)}
                      placeholder="Property address"
                    />
                    <button
                      className="btn-icon btn-icon-danger"
                      onClick={() => deleteEntry(entry)}
                      title="Delete this lease"
                    >
                      ×
                    </button>
                  </div>
                  <div className="lease-fields">
                    {fields.map(field => {
                      const isAuto = field.name in AUTO_FIELDS;
                      return (
                        <div key={field.name} className="lease-field">
                          <label>{field.name}{isAuto && <span className="auto-tag"> (auto)</span>}</label>
                          <input
                            type="text"
                            value={data[field.name] || ''}
                            onChange={e => updateField(entry.id, field.name, e.target.value)}
                            disabled={isAuto}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="lease-card-actions">
                    <button
                      className="btn-primary"
                      onClick={() => handleFillPdf(entry)}
                      disabled={!!filling[entry.id]}
                    >
                      {filling[entry.id] ? 'Filling...' : 'Fill PDF'}
                    </button>
                  </div>
                </div>
              );
            })}

            <button className="btn-secondary btn-add-lease" onClick={addEntry}>
              + Add Another Lease
            </button>
          </div>

          <aside className="leases-sidebar">
            <h2 className="sidebar-title">Filled Leases for this Refi Pack</h2>
            {leases.length === 0 ? (
              <p className="sidebar-empty">No filled leases yet. Fill out the fields above and click "Fill PDF".</p>
            ) : (
              <ul className="lease-list">
                {leases.map(ls => (
                  <li key={ls.id} className="lease-list-item">
                    <div className="lease-list-info">
                      <div className="lease-list-address">{ls.address}</div>
                      <div className="lease-list-meta">{ls.file_name}</div>
                    </div>
                    <div className="lease-list-actions">
                      <button
                        className="btn-icon"
                        onClick={() => handleDownload(ls)}
                        title="Download"
                      >
                        ↓
                      </button>
                      <button
                        className="btn-icon btn-icon-danger"
                        onClick={() => handleDeleteFile(ls)}
                        title="Delete"
                      >
                        ×
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}
